import { Loader } from 'components';
import { queryObject } from 'helpers';
import { useConfirmEmail, useResendEmailVerification, useToast } from 'hooks';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Routes } from 'router';

/**
 * Interface for confirm email payload
 */
export interface ConfirmEmailPayload {
  email: string;
  token: string;
}

/**
 * Interface for resend email verification payload
 */
export interface ResendEmailVerificationPayload {
  email: string;
}

const EmailVerification = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { openToast } = useToast();

  const { email, token } = queryObject(search);

  // Hooks
  const { mutate: confirmEmail, data: verificationResponse, isPending: confirmPending, error: verificationError } = useConfirmEmail();
  const { mutate: resendEmailVerification, isPending: resendPending } = useResendEmailVerification();

  const handleVerification = () => {
    if (email && token) {
      confirmEmail({ email, token });
    }
  };

  useEffect(() => {
    handleVerification();
  }, [email, token]);

  // Handle successful verification - redirect to sign in
  useEffect(() => {
    if (verificationResponse?.data) {
      // Show success toast
      openToast({
        show: true,
        heading: 'Email Verified!',
        text: 'Your account has been verified successfully. Please sign in to continue.',
        type: 'success',
        timeOut: 2000,
      });

      // Navigate to sign in page
      navigate(Routes.signin, { replace: true });
    }
  }, [verificationResponse]);

  const handleResend = () => {
    if (email) {
      resendEmailVerification({ email });
    }
  };

  const showLoader = confirmPending || resendPending;

  // Show error UI if verification failed
  if (verificationError && !confirmPending) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Verification Failed</h1>
          <p className="text-gray-600 mb-6">
            The verification link has expired or is invalid. Please request a new verification email.
          </p>
          <button
            onClick={handleResend}
            disabled={resendPending}
            className="w-full bg-[#012169] text-white py-3 px-4 rounded-md font-medium hover:bg-[#012169]/90 transition-colors disabled:opacity-50"
          >
            {resendPending ? 'Sending...' : 'Resend Verification Email'}
          </button>
        </div>
      </main>
    );
  }

  // Show loader while verifying
  return <Loader loading={showLoader || confirmPending} />;
};

export { EmailVerification };
